import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { PageContainer } from '../components/Container/PageContainer';
import { dbService } from '../firebaseInstance';
import { getPublicAvatarUrl } from '../utils/publicAvatarUtils';
import ParticipantShowcaseCard from '../template/magazine/ParticipantShowcaseCard';
import { useIsMobile } from '../hooks/useIsMobile';
import { subscribeCategories } from '../services/adminCategoryManager';
import CategoryChipsBar from '../components/chips/CategoryChipsBar';
import AlphabetIndexBar from '../components/navigation/AlphabetIndexBar';

const ThreadsPeople = () => {
  const isMobile = useIsMobile('md');
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeLetter, setActiveLetter] = useState(null);

  useEffect(() => {
    let unsub = null;

    const subscribe = (withOrderBy = true) => {
      try {
        let q = dbService
          .collection('threadsPeople')
          .where('isActive', '==', true);
        if (withOrderBy) {
          q = q.orderBy('updatedAt', 'desc');
        }

        unsub = q.onSnapshot(
          (snap) => {
            const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            // 클라이언트 정렬(보조): updatedAt 내림차순
            const sorted = data
              .slice()
              .sort((a, b) => {
                const av = a?.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : (a?.updatedAt?.seconds ? a.updatedAt.seconds * 1000 : (a?.updatedAt || 0));
                const bv = b?.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : (b?.updatedAt?.seconds ? b.updatedAt.seconds * 1000 : (b?.updatedAt || 0));
                return bv - av;
              });
            setList(sorted);
          },
          (err) => {
            console.warn('[ThreadsPeople] onSnapshot error', err?.message || err);
            // orderBy 인덱스 문제 등으로 실패 시, orderBy 없이 재구독
            if (withOrderBy) {
              try { unsub && unsub(); } catch (_) { }
              subscribe(false);
            } else {
              // no-op
            }
          }
        );
      } catch (e) {
        console.warn('[ThreadsPeople] subscribe failed', e?.message || e);
        // no-op
      }
    };

    subscribe(true);

    return () => {
      try { unsub && unsub(); } catch (_) { }
    };
  }, []);

  // 카테고리 구독 (라벨용)
  useEffect(() => {
    const unsub = subscribeCategories(
      (list) => setCategories(list.filter((c) => c.isActive !== false)),
      (e) => console.error('Error subscribing categories:', e)
    );
    return () => unsub && unsub();
  }, []);

  // 선택된 카테고리 기준 목록 필터
  const filteredList = useMemo(() => {
    if (!selectedCategory) return list;
    return list.filter((p) => Array.isArray(p.categoryKeys) && p.categoryKeys.includes(selectedCategory));
  }, [list, selectedCategory]);

  // username 첫 글자 그룹핑 (0-9, A-Z, #)
  const HEADER_OFFSET = isMobile ? 56 : 64; // 헤더 높이 기준 오프셋
  const getInitial = (username = '') => {
    const c = (username || '').trim().charAt(0) || '';
    if (/[0-9]/.test(c)) return '0-9';
    const u = c.toUpperCase();
    if (/[A-Z]/.test(u)) return u;
    return '#';
  };

  const groupedSections = useMemo(() => {
    const map = new Map();
    filteredList.forEach((p) => {
      const key = getInitial(p?.username || p?.name || '');
      if (!map.has(key)) {
        map.set(key, { key, label: key, id: `letter-${key}`, items: [] });
      }
      map.get(key).items.push(p);
    });
    const order = ['0-9', ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)), '#'];
    const arr = order
      .filter((k) => map.has(k))
      .map((k) => {
        const sec = map.get(k);
        sec.items.sort((a, b) => (a?.username || '').localeCompare(b?.username || '', undefined, { sensitivity: 'base' }));
        return sec;
      });
    return arr;
  }, [filteredList]);

  // 알파벳 인덱스 3개씩 묶기 (A–C, D–F, ...), 0-9 / #는 단독
  const triadIndexItems = useMemo(() => {
    const sectionByKey = new Map(groupedSections.map((s) => [s.key, s]));
    const groups = [];
    // 0-9 그룹
    if (sectionByKey.has('0-9')) {
      const sec = sectionByKey.get('0-9');
      groups.push({ key: '0-9', label: '0-9', targetId: sec.id, count: sec.items.length, keys: ['0-9'] });
    }
    // A-Z triads
    const letters = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
    for (let i = 0; i < letters.length; i += 3) {
      const slice = letters.slice(i, i + 3);
      const existing = slice.filter((k) => sectionByKey.has(k));
      if (existing.length === 0) continue;
      const first = existing[0];
      const count = existing.reduce((sum, k) => sum + (sectionByKey.get(k)?.items?.length || 0), 0);
      const label = `${slice[0]}–${slice[slice.length - 1]}`;
      groups.push({
        key: label,
        label,
        targetId: sectionByKey.get(first).id,
        count,
        keys: slice,
      });
    }
    // # 그룹
    if (sectionByKey.has('#')) {
      const sec = sectionByKey.get('#');
      groups.push({ key: '#', label: '#', targetId: sec.id, count: sec.items.length, keys: ['#'] });
    }
    return groups;
  }, [groupedSections]);

  const activeTriadKey = useMemo(() => {
    if (!activeLetter) return null;
    const found = triadIndexItems.find((g) => g.keys.includes(activeLetter));
    return found ? found.key : null;
  }, [activeLetter, triadIndexItems]);

  // 우측 컨텐츠도 알파벳 3개 단위(트라이어드)로 묶어서 렌더링
  const triadContentSections = useMemo(() => {
    const sectionByKey = new Map(groupedSections.map((s) => [s.key, s]));
    const groups = [];
    // 0-9 단독 섹션
    if (sectionByKey.has('0-9')) {
      const sec = sectionByKey.get('0-9');
      groups.push({
        key: '0-9',
        label: '0-9',
        id: sec.id, // 인덱스 타겟과 동일하게 첫 섹션 id 사용
        firstKey: '0-9',
        items: sec.items.slice(),
      });
    }
    // A-Z를 3개씩 묶기
    const letters = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
    for (let i = 0; i < letters.length; i += 3) {
      const slice = letters.slice(i, i + 3);
      const existing = slice.filter((k) => sectionByKey.has(k));
      if (existing.length === 0) continue;
      const first = existing[0];
      const id = sectionByKey.get(first).id;
      const aggregated = [];
      existing.forEach((k) => {
        const s = sectionByKey.get(k);
        if (s && Array.isArray(s.items)) aggregated.push(...s.items);
      });
      aggregated.sort((a, b) => (a?.username || '').localeCompare(b?.username || '', undefined, { sensitivity: 'base' }));
      const label = `${slice[0]}–${slice[slice.length - 1]}`;
      groups.push({ key: label, label, id, firstKey: first, items: aggregated });
    }
    // # 단독 섹션
    if (sectionByKey.has('#')) {
      const sec = sectionByKey.get('#');
      groups.push({
        key: '#',
        label: '#',
        id: sec.id,
        firstKey: '#',
        items: sec.items.slice(),
      });
    }
    return groups;
  }, [groupedSections]);

  // 카테고리 요약(count, label)
  const totalCount = list.length;
  const categorySummaryList = useMemo(() => {
    const countMap = new Map();
    list.forEach((p) => {
      const keys = Array.isArray(p.categoryKeys) ? p.categoryKeys : [];
      keys.forEach((k) => countMap.set(k, (countMap.get(k) || 0) + 1));
    });
    const labelOf = (key) => {
      const found = categories.find((c) => c.key === key);
      return (found && (found.labels?.ko || found.labels?.en)) || key;
    };
    const arr = Array.from(countMap.entries()).map(([key, count]) => ({ key, label: labelOf(key), count }));
    return arr.sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label));
  }, [list, categories]);

  // Note: 섹션 렌더에서 직접 매핑하므로 별도 items 메모는 생략

  // IntersectionObserver로 활성 섹션 하이라이트
  useEffect(() => {
    const heads = document.querySelectorAll('[data-letter-head="1"]');
    if (!heads || heads.length === 0) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top));
        if (visible.length > 0) {
          const key = visible[0].target.getAttribute('data-letter-key');
          if (key) setActiveLetter(key);
        }
      },
      {
        root: null,
        rootMargin: `-${HEADER_OFFSET + 8}px 0px -80% 0px`,
        threshold: [0, 0.1],
      }
    );

    heads.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [groupedSections, HEADER_OFFSET]);

  const handleIndexClick = (key, targetId) => {
    const el = document.getElementById(targetId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <PageContainer expand>
      <Stack spacing={4} sx={{ width: '100%' }}>
        <Stack spacing={1}>
          <Typography variant="h4" fontWeight={600}>Threads 인물 사전</Typography>
          <Typography variant="body1" color="text.secondary">등록된 작가/참여자 목록</Typography>
        </Stack>
        {/* Sticky wrapper for CategoryChipsBar */}
        <Box sx={{ position: 'sticky', top: HEADER_OFFSET, zIndex: (theme) => theme.zIndex.appBar - 1, backgroundColor: 'background.default' }}>
          <CategoryChipsBar
            totalCount={totalCount}
            categories={categorySummaryList}
            selectedCategory={selectedCategory}
            onCategoryClick={(key) => setSelectedCategory(key === 'all' ? null : key)}
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, md: 2 }, maxWidth: '100%' }}>
          <AlphabetIndexBar
            letters={triadIndexItems}
            activeKey={activeTriadKey}
            onClick={handleIndexClick}
            offset={HEADER_OFFSET}
            hiddenOnMobile={false}
          />

          <Box sx={{ flex: 1, minWidth: 0, overflowX: 'hidden', width: '100%' }}>
            {triadContentSections.map((group) => (
              <Box key={group.key} sx={{ mb: 4 }}>
                <Typography
                  id={group.id}
                  data-letter-head="1"
                  data-letter-key={group.firstKey}
                  variant="subtitle2"
                  fontWeight={800}
                  sx={{
                    color: 'text.secondary',
                    letterSpacing: '0.08em',
                    mb: 1.5,
                    scrollMarginTop: `${HEADER_OFFSET + 12}px`,
                  }}
                >
                  {group.label} ({group.items.length})
                </Typography>

                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  {group.items.map((p) => {
                    const u = {
                      id: p.id,
                      profile: {
                        id: p.id,
                        username: p.username || '',
                        name: p.name || '',
                        threads_profile_picture_url: getPublicAvatarUrl(String(p.id), 320),
                      },
                      __fallbackAvatar: getPublicAvatarUrl(String(p.id), 320),
                      __personBio: p.bio || '',
                      __personBusinessLink: p.businessLink || null,
                      categoryLabels: Array.isArray(p?.categoryLabels) ? p.categoryLabels : [],
                    };
                    return (
                      <Grid key={u.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <ParticipantShowcaseCard
                          user={u}
                          isMobile={isMobile}
                          fallbackAvatarUrl={u.__fallbackAvatar}
                          bio={u.__personBio}
                          businessLink={u.__personBusinessLink}
                          categoryLabels={u.categoryLabels}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            ))}
          </Box>
        </Box>
      </Stack>
    </PageContainer>
  );
};

export default ThreadsPeople;


